import { type FC } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { HStack, Item, MenuOptionItem, MenuTriggerButton, Spacer, TriggerButtonSize } from "widgets";

import { openInSyntenyViewerTab } from "@/api/syntenyViewer.ts";
import { canSendToSyntenyViewer } from "@/app-utils/entity.ts";
import { mapSequenceTypeToEntityType } from "@/app-utils/route.ts";
import { type UserGroup } from "@/graphql.ts";
import { ENTITY_TYPE } from "@/settings/settings.js";
import { type IUserGroupAdminContext } from "@/ui-modules/content-browser/ContentBrowserTableView.tsx";
import { type IAgGridCellProps } from "@/ui-modules/core/grid/cell-base-interfaces.ts";
import { SmartLink } from "@/ui-modules/core/SmartLink.tsx";
import { SampleLockedIcon } from "@/ui-modules/core/tiles/components/SampleTile.tsx";

import { TableCell } from "./TableCell.js";
import TableCellText from "./TableCellText.js";

// eslint-disable-next-line complexity
const LinkCell: FC<IAgGridCellProps> = (props) => {
  // LinkCell optionally receives a context inherited from ag-grid options.

  const { value, data, colDef, context } = props;

  const navigate = useNavigate();
  const [searchParams, _] = useSearchParams();

  if (!data || !value) {
    return null;
  }
  const useExternal = colDef.openLinkExternal;
  const { id, mutId, groupType, genome } = data;
  const field = colDef.field;

  function createSummaryLink(valueStr: string, entityGroupType: string, entityId: number) {
    return (
      <TableCell>
        <HStack>
          <SummaryPageLink
            linkLabel={valueStr}
            entityGroupType={entityGroupType}
            entityId={entityId}
            useExternal={useExternal}
          />
          {/* Only on Summary pages, see SEL11-806 */}
          {/* TODO: Menu Button is misaligned compared to the same button in the column header */}
          {context?.tableSpec?.idType === "entity" &&
          context?.genomeSequencingType &&
          canSendToSyntenyViewer(entityGroupType, context?.genomeSequencingType) ? (
            <>
              <Spacer />
              <MenuTriggerButton
                icon="more_vert"
                label=""
                aria-label="Open in Synteny Viewer"
                size={TriggerButtonSize.Small}
                onAction={(key) => {
                  switch (key) {
                    case "open-in-synteny":
                      if (context?.tableSpec?.id) {
                        const viewid = searchParams.get("viewid");
                        navigate(
                          openInSyntenyViewerTab({
                            syntenyGenomeId: context.tableSpec.id,
                            organismId: genome.id,
                            entityId: id,
                            featureType: {
                              resultType: context.tableSpec.resultType,
                              resultSubTypeId: context.tableSpec.resultSubTypeId,
                            },
                            viewid: viewid ? viewid : undefined,
                          }),
                        );
                      }
                  }
                }}
              >
                <Item key={"open-in-synteny"}>
                  <MenuOptionItem id="open-in-synteny" title={"Send to Synteny Viewer"} icon={"arrow_outward"} />
                </Item>
              </MenuTriggerButton>
            </>
          ) : null}
        </HStack>
      </TableCell>
    );
  }

  function createSampleSummaryLink(valueStr: string, entityGroupType: string, entityId: number, locked?: boolean) {
    return (
      <TableCell alternativeButton={locked ? <SampleLockedIcon size={15} /> : null}>
        <SummaryPageLink
          linkLabel={valueStr}
          entityGroupType={entityGroupType}
          entityId={entityId}
          useExternal={useExternal}
        />
      </TableCell>
    );
  }

  function createGenomeViewerLink(valueStr: string, entityGroupType: string, entityId: number, genomeId: number) {
    return (
      <TableCell>
        <GenomeViewerLink
          linkLabel={valueStr}
          entityGroupType={entityGroupType}
          entityId={entityId}
          genomeId={genomeId}
        />
      </TableCell>
    );
  }

  // Usually _all_ listed genomes are visible to the current user because the list is filterd for the user in the middle-tier..
  // In admin mode some additional genomes are listed that are not accessible to the current user.
  //   For admin mode access is controlled by user groups assigned to the user and the genome.
  //   This check protects the admin from running into an error page when he accesses the summary page of such a genome.
  function isAccessPermitted(): boolean {
    return (
      !context?.userGroupAdminContext ||
      atLeastOneOverlap((context.userGroupAdminContext as IUserGroupAdminContext).user.userGroups, data.userGroups)
    );
  }

  // simple links from list to summary of same group type
  //   UG test for contigs is relevant in admin mode
  if (
    (groupType === ENTITY_TYPE.GENE && field === "name") ||
    (groupType === ENTITY_TYPE.ORGANISM && (field === "name" || field === "identifier") && isAccessPermitted()) ||
    (groupType === ENTITY_TYPE.PROTEIN && field === "sequenceId") ||
    (groupType === ENTITY_TYPE.PROTEIN && field === "accession") ||
    (groupType === ENTITY_TYPE.PROTEIN && field === "name") ||
    (groupType === ENTITY_TYPE.TRANSCRIPT && field === "sequenceId") ||
    (groupType === ENTITY_TYPE.TRANSCRIPT && field === "accession") ||
    (groupType === ENTITY_TYPE.CONTIG && field === "sequenceId") ||
    (groupType === ENTITY_TYPE.CONTIG && field === "accession") ||
    (groupType === ENTITY_TYPE.MARKER && field === "name") ||
    (groupType === ENTITY_TYPE.NUFEATURE && field === "name") ||
    (groupType === ENTITY_TYPE.AAFEATURE && field === "name") ||
    (groupType === ENTITY_TYPE.OTHER && field === "sequenceId") ||
    (groupType === ENTITY_TYPE.PROBE && field === "sequenceId")
  ) {
    return createSummaryLink(value, groupType, id);
  } else if (groupType === ENTITY_TYPE.SAMPLE && field === "name") {
    return createSampleSummaryLink(value, groupType, id, data.locked);
  } else if (groupType === ENTITY_TYPE.SEQUENCE) {
    const sequenceGroupType: string = data.sequenceGroup.name;
    return createSummaryLink(value, mapSequenceTypeToEntityType(sequenceGroupType), id);
  } else if (groupType === ENTITY_TYPE.VARIANT && field === "name") {
    // If we are in the variant -> "related variants" tab, link to the genome view,
    // if we are in the organism -> variants tab, just link to the summary.
    return context?.tableSpec && context.tableSpec.queryType === ENTITY_TYPE.MUTATION
      ? createGenomeViewerLink(value, ENTITY_TYPE.VARIANT, mutId, data.genome.id)
      : createSummaryLink(value, ENTITY_TYPE.VARIANT, mutId);
  } else if (groupType === ENTITY_TYPE.MUTATION && field === "name") {
    return createSummaryLink(value, ENTITY_TYPE.VARIANT, id);
  } else if (
    // Transcripts table in Genome Viewer
    groupType === ENTITY_TYPE.TRANSCRIPT_LOCATION &&
    (field === "transcript.sequenceId" || field === "transcript.accession" || field === "transcript.name")
  ) {
    return createSummaryLink(value, ENTITY_TYPE.TRANSCRIPT, data.transcript.id);
  } else if (
    (groupType === ENTITY_TYPE.GENE_MUTATION || groupType === ENTITY_TYPE.PROTEIN_MUTATION) &&
    field === "variant.name"
  ) {
    return createSummaryLink(value, ENTITY_TYPE.VARIANT, data.variant.mutId);
  } else if (
    (groupType === ENTITY_TYPE.PROTEIN_LOCATION || groupType === ENTITY_TYPE.TRANSCRIPT) &&
    (field === "protein.sequenceId" || field === "protein.name" || field === "protein.accession")
  ) {
    return createSummaryLink(value, ENTITY_TYPE.PROTEIN, data.protein.id);
  } else if (groupType === ENTITY_TYPE.ORTHOLOG || groupType === ENTITY_TYPE.DIRECTIONAL_ORTHOLOG) {
    return createSummaryLink(value, ENTITY_TYPE.PROTEIN, data.protein.id);
  } else if (
    (groupType === ENTITY_TYPE.PROTEIN_MUTATION ||
      groupType === ENTITY_TYPE.ORTHOLOG ||
      groupType === ENTITY_TYPE.DIRECTIONAL_ORTHOLOG) &&
    (field === "proteinLocation.protein.sequenceId" ||
      field === "proteinLocation.protein.contig.sequenceId" ||
      field === "proteinLocation.protein.name")
  ) {
    return createSummaryLink(value, ENTITY_TYPE.PROTEIN, data.proteinLocation.protein.id);

    // links switching the entity (e.g. a gene link in the transcript table)
  } else if (
    (groupType === ENTITY_TYPE.TRANSCRIPT ||
      groupType === ENTITY_TYPE.PROTEIN ||
      groupType === ENTITY_TYPE.GENE_MUTATION) &&
    field === "gene.name"
  ) {
    return createSummaryLink(value, ENTITY_TYPE.GENE, data.gene.id);
  } else if (
    (groupType === ENTITY_TYPE.GENE ||
      groupType === ENTITY_TYPE.VARIANT ||
      groupType === ENTITY_TYPE.PROTEIN_LOCATION ||
      groupType === ENTITY_TYPE.TRANSCRIPT_LOCATION ||
      groupType === ENTITY_TYPE.REGION_FLAG ||
      groupType === ENTITY_TYPE.BED_FEATURE ||
      groupType === ENTITY_TYPE.COORDINATE ||
      groupType === ENTITY_TYPE.MARKER ||
      groupType === ENTITY_TYPE.MARKER_NUS ||
      groupType === ENTITY_TYPE.NUFEATURE) &&
    field === "region.contig.sequenceId"
  ) {
    return createSummaryLink(value, ENTITY_TYPE.CONTIG, data.region.contig.id);
  } else if (
    (groupType === ENTITY_TYPE.PROTEIN_MUTATION || groupType === ENTITY_TYPE.GENE_MUTATION) &&
    field === "variant.region.contig.sequenceId"
  ) {
    return createSummaryLink(value, ENTITY_TYPE.CONTIG, data.variant.region.contig.id);
  } else if (groupType === ENTITY_TYPE.GENE_MUTATION && field === "gene.region.contig.sequenceId") {
    return createSummaryLink(value, ENTITY_TYPE.CONTIG, data.gene.region.contig.id);
  } else if (groupType === ENTITY_TYPE.PROTEIN_MUTATION && field === "proteinLocation.region.contig.sequenceId") {
    return createSummaryLink(value, ENTITY_TYPE.CONTIG, data.proteinLocation.region.contig.id);
  } else if (groupType === ENTITY_TYPE.PROTEIN_MUTATION && field === "proteinLocation.protein.transcripts.sequenceId") {
    return createSummaryLink(value, ENTITY_TYPE.TRANSCRIPT, data.proteinLocation.protein.transcripts.id);
  } else if (groupType === ENTITY_TYPE.MUTATION && field === "refVariant.region.contig.sequenceId") {
    return createSummaryLink(value, ENTITY_TYPE.CONTIG, data.refVariant.region.contig.id);
  } else if (groupType === ENTITY_TYPE.MUTATION && field === "refVariant.refRegion.contig.sequenceId") {
    return createSummaryLink(value, ENTITY_TYPE.CONTIG, data.refVariant.refRegion.contig.id);
  } else if (
    (groupType === ENTITY_TYPE.PROTEIN_MUTATION || groupType === ENTITY_TYPE.GENE_MUTATION) &&
    field === "variant.refRegion.contig.sequenceId"
  ) {
    return createSummaryLink(value, ENTITY_TYPE.CONTIG, data.variant.refRegion.contig.id);
  } else if (groupType === ENTITY_TYPE.VARIANT && field === "refRegion.contig.sequenceId") {
    return createSummaryLink(value, ENTITY_TYPE.CONTIG, data.refRegion.contig.id);
  } else if (groupType === ENTITY_TYPE.REGION_FLAG && field === "name") {
    // special link to show the flag in the genome viewer and in the flag table of genome summaries
    return createFlagLink(value, data.id, colDef);
  } else if (
    groupType === ENTITY_TYPE.ORTHOLOG ||
    (groupType === ENTITY_TYPE.DIRECTIONAL_ORTHOLOG && field === "protein.gene.name")
  ) {
    return createSummaryLink(value, ENTITY_TYPE.GENE, data.protein.gene.id);
  } else if (groupType === ENTITY_TYPE.MARKER_NUS && field === "marker.name") {
    // In the genome viewer context, the Marker table is MARKER_NUS (only a single location). The summary page, however,
    // is a MARKER summary page
    return createSummaryLink(value, ENTITY_TYPE.MARKER, data.marker.id);
  } else if (groupType === ENTITY_TYPE.MUTATION && field === "refVariant.name") {
    return createSummaryLink(value, ENTITY_TYPE.VARIANT, data.id);
  } else if (groupType === ENTITY_TYPE.AAFEATURE && field === "protein.sequenceId") {
    return createSummaryLink(value, ENTITY_TYPE.PROTEIN, data.protein.id);
  } else if (groupType === ENTITY_TYPE.NUFEATURE && field === "gene.name") {
    return createSummaryLink(value, ENTITY_TYPE.GENE, data.gene.id);
  } else {
    // default renderer, unlinked
    return <TableCellText {...props} />;
  }
};

interface SummaryPageLinkProps {
  linkLabel: string;
  entityGroupType: string;
  entityId: number;
  useExternal?: boolean;
}

const SummaryPageLink: FC<SummaryPageLinkProps> = (props) => {
  const { linkLabel, entityGroupType, entityId, useExternal } = props;
  return (
    <SmartLink to={`/${entityGroupType}/${entityId}`} forceExternal={useExternal} tooltip={linkLabel}>
      {linkLabel}
    </SmartLink>
  );
};

interface GenomeViewerLinkProps {
  linkLabel: string;
  entityGroupType: string;
  entityId: number;
  genomeId: number;
}

const GenomeViewerLink: FC<GenomeViewerLinkProps> = (props) => {
  const { linkLabel, entityGroupType, entityId, genomeId } = props;
  return (
    <SmartLink to={`/${entityGroupType}/${entityId}/genome/${genomeId}`} forceExternal={true} tooltip={linkLabel}>
      {linkLabel}
    </SmartLink>
  );
};

function createFlagLink(valueStr: string, flagId: number, myColDef: any) {
  const href = `/visualizer/genome?flag=${flagId}`;

  // SEL6-2682: This is a special workaround for a link problem leading to an annoying re-init of the feature table in the Genome Viewer.
  // There, the link is now disabled and we entirely rely on the row click.
  const onClickFunc = myColDef.openLinkExternal
    ? (e: any) => {
        e.preventDefault();
      }
    : null;
  // use display: 'inline-grid', otherwise the div inherits 'inline' which disables the ellipsis in some browsers ...
  return (
    <TableCell>
      <SmartLink to={href} onClick={onClickFunc} tooltip={valueStr}>
        {valueStr}
      </SmartLink>
    </TableCell>
  );
}

function atLeastOneOverlap(userUGList: UserGroup[], dataUGList: UserGroup[]): boolean {
  return (
    !!userUGList &&
    userUGList.length > 0 &&
    !!dataUGList &&
    dataUGList.length > 0 &&
    !!dataUGList.find((dg: UserGroup) => !!userUGList.find((ug: UserGroup) => dg.id === ug.id))
  );
}

export { LinkCell, SummaryPageLink };
